import request from '@/modules/base/util/request'
import config from '@/config'
import { toast } from '@/modules/base/components/toast/toastConfig'
import lang from '@/lang'

type TransformType = 'shorten' | 'lengthen' | 'tone' | 'spellcheck' | 'translate'
type Tone = 'professional' | 'simple'
type TranslateLanguage = 'en' | 'de'

interface TransformTextRequestData {
  text: string
  targetLanguage?: TranslateLanguage
  tone?: Tone
}

interface TransformTextRequest {
  transformType: TransformType
  data: TransformTextRequestData
}

class AiService {
  /**
   * Transformiert Text mithilfe von KI
   * @param text - Der zu transformierende Text
   * @param optionId - Die ID der gewählten Transformation
   *   Format: 'shorten', 'lengthen', 'spellcheck', 'tone-{tone}', 'translate-{language}'
   * @returns Der transformierte Text
   */
  async transformText(text: string, optionId: string): Promise<string> {
    try {
      const requestData = this.parseOptionId(text, optionId)

      const response = await request(
        config.resourceHost + 'ai/transform/text',
        {
          auth: true,
          isConcurrent: true,
          method: 'POST',
          data: requestData,
        },
      )

      return response.body.data.text as string
    } catch (error) {
      const message = error?.body?.message
      if (message) {
        toast.showToast({ type: 'error', message })
      } else {
        toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
      }

      throw error
    }
  }

  /**
   * Parst die optionId und erstellt das passende Request-Objekt
   * @private
   */
  private parseOptionId(text: string, optionId: string): TransformTextRequest {
    // Handle tone options (e.g., 'tone-professional', 'tone-simple')
    if (optionId.startsWith('tone-')) {
      const tone = optionId.replace('tone-', '') as Tone
      return {
        transformType: 'tone',
        data: { text, tone },
      }
    }

    // Handle translate options (e.g., 'translate-en', 'translate-de')
    if (optionId.startsWith('translate-')) {
      const targetLanguage = optionId.replace('translate-', '') as TranslateLanguage
      return {
        transformType: 'translate',
        data: { text, targetLanguage },
      }
    }

    // Handle simple transform types
    return {
      transformType: optionId as TransformType,
      data: { text },
    }
  }
}

export default new AiService()
